import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { Order } from '~hooks/useOrders/models';
import useDefaultSitesQuery from '~queries/useDefaultSitesQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { computeReplaySegments, ReplayRow } from './computeReplaySegments';
import { getReplayTruckColor } from './replayColors';
import { HighlightedLoad } from './replayInteractions';
import { buildReplaySites } from './replaySites';
import TruckReplayGantt from './TruckReplayGantt';
import { getTruckReplayRowLabel } from './truckReplayLabels';
import TruckReplayMap, { ReplayMapTruck } from './TruckReplayMap';
import TruckReplaySkeleton from './TruckReplaySkeleton';
import useOrderReplayData, { OrderReplayData } from './useOrderReplayData';
import useReplayScrubber from './useReplayScrubber';

interface TruckReplayTabProps {
  order: Order;
}

const TruckReplayTab = observer(({ order }: TruckReplayTabProps) => {
  const theme = useTheme();
  const { userStore } = useStores();
  const companyId = order.company?.id ?? getEffectiveUserCompanyId(userStore);

  const { fetchReplayData } = useOrderReplayData();
  const { defaultSites } = useDefaultSitesQuery({ companyId, orderId: order.id });

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [data, setData] = useState<OrderReplayData | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const result = await fetchReplayData(companyId, order.id, controller.signal);
        if (controller.signal.aborted) return;

        setData(result);
        AnalyticsController.track('truck_replay_loaded', {
          order_id: order.id,
          truck_count: result.jobs.length,
          outcome: result.jobs.length > 0 ? 'has_data' : 'no_gps_data',
        });
      } catch {
        if (controller.signal.aborted) return;

        setHasError(true);
        AnalyticsController.track('truck_replay_loaded', {
          order_id: order.id,
          truck_count: 0,
          outcome: 'error',
        });
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    void load();

    return () => controller.abort();
  }, [companyId, order.id, fetchReplayData]);

  const sites = useMemo(
    () => buildReplaySites(order, defaultSites),
    [order, defaultSites],
  );

  const trucks = useMemo<ReplayMapTruck[]>(() => {
    if (!data) return [];

    return data.jobs.map((job, index) => ({
      color: getReplayTruckColor(index),
      jobId: job.id,
      label: getTruckReplayRowLabel(job).truckNumber,
      pings: data.pingsByJobId.get(job.id) ?? [],
    }));
  }, [data]);

  const [minTimeMs, maxTimeMs] = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    for (const truck of trucks) {
      for (const ping of truck.pings) {
        const ms = ping.occurredAt.valueOf();
        if (ms < min) min = ms;
        if (ms > max) max = ms;
      }
    }

    return Number.isFinite(min) ? [min, max] : [0, 0];
  }, [trucks]);

  const rows = useMemo<ReplayRow[]>(() => {
    if (!data) return [];

    return data.jobs.map((job) => ({
      key: job.id,
      label: getTruckReplayRowLabel(job),
      segments: computeReplaySegments(data.pingsByJobId.get(job.id) ?? []),
    }));
  }, [data]);

  const [isMapCollapsed, setIsMapCollapsed] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [highlightedLoad, setHighlightedLoad] = useState<HighlightedLoad | null>(null);

  const {
    isLocked,
    isPlaying,
    replayFraction,
    replayTimeMs,
    setIsLocked,
    setReplayFraction,
    togglePlay,
  } = useReplayScrubber(minTimeMs, maxTimeMs);

  useEffect(() => {
    setSelectedJobId(null);
    setHighlightedLoad(null);
    setIsLocked(false);
  }, [order.id, setIsLocked]);

  const handleSelectTruck = useCallback(
    (jobId: string) => {
      const isSelectingTruck = selectedJobId !== jobId;
      if (isSelectingTruck) {
        AnalyticsController.track('truck_replay_truck_selected', {
          order_id: order.id,
        });
      }
      setSelectedJobId(isSelectingTruck ? jobId : null);
      setHighlightedLoad(null);
    },
    [selectedJobId, order.id],
  );

  const handleHighlightLoad = useCallback(
    (load: HighlightedLoad) => {
      const isSameLoad =
        highlightedLoad?.jobId === load.jobId && highlightedLoad?.loadId === load.loadId;
      if (!isSameLoad) {
        AnalyticsController.track('truck_replay_load_highlighted', {
          order_id: order.id,
        });
      }
      setHighlightedLoad(isSameLoad ? null : load);
    },
    [highlightedLoad, order.id],
  );

  const handleLockChange = useCallback(
    (locked: boolean) => {
      if (locked && !isLocked) {
        AnalyticsController.track('truck_replay_time_locked', { order_id: order.id });
      }
      setIsLocked(locked);
    },
    [isLocked, setIsLocked, order.id],
  );

  // Track only the transition into playback — the "actively watching the replay"
  // signal. Pausing is not its own engagement event.
  const handleTogglePlay = useCallback(() => {
    if (!isPlaying) {
      AnalyticsController.track('truck_replay_played', { order_id: order.id });
    }
    togglePlay();
  }, [isPlaying, togglePlay, order.id]);

  // Trucks are derived from ping jobIds, so a non-empty list means GPS data
  // exists — including the single-ping case where the time span is zero (a
  // freshly started or low-frequency/offline-upload job still has a position).
  const hasReplayData = trucks.length > 0;

  if (isLoading) {
    return <TruckReplaySkeleton progressLabel={t('truck_replay.loading')} />;
  }

  if (hasError) {
    return (
      <Centered>
        <Typography variant="body2" sx={{ color: theme.brandV2.colors.treadGray3 }}>
          {t('error_messages.truck_replay.failed_to_fetch')}
        </Typography>
      </Centered>
    );
  }

  if (!hasReplayData) {
    return (
      <Centered>
        <Typography variant="body2" sx={{ color: theme.brandV2.colors.treadGray3 }}>
          {t('truck_replay.no_gps_data')}
        </Typography>
      </Centered>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, p: 1 }}>
      {/* Gantt column carries its own scrubber below the rows; collapsing the
          map lets the Gantt run full width. */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <TruckReplayGantt
          highlightedLoad={highlightedLoad}
          isLocked={isLocked}
          isPlaying={isPlaying}
          maxTimeMs={maxTimeMs}
          minTimeMs={minTimeMs}
          onHighlightLoad={handleHighlightLoad}
          onLockChange={handleLockChange}
          onScrubFraction={setReplayFraction}
          onSelectTruck={handleSelectTruck}
          onTogglePlay={handleTogglePlay}
          replayFraction={replayFraction}
          replayTimeMs={replayTimeMs}
          rows={rows}
          selectedJobId={selectedJobId}
        />
      </Box>

      {isMapCollapsed ? (
        <SmallButton
          color="brandV2TreadGray3"
          onClick={() => setIsMapCollapsed(false)}
          startIcon={<ChevronLeft />}
          variant="outlined"
          sx={{ alignSelf: 'flex-start', flexShrink: 0 }}
        >
          {t('truck_replay.show_map')}
        </SmallButton>
      ) : (
        <Box
          sx={{ display: 'flex', flex: '0 0 38%', flexDirection: 'column', minWidth: 0 }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <SmallButton
              color="brandV2TreadGray3"
              endIcon={<ChevronRight />}
              onClick={() => setIsMapCollapsed(true)}
              variant="text"
            >
              {t('truck_replay.collapse')}
            </SmallButton>
          </Box>
          <TruckReplayMap
            highlightedLoad={highlightedLoad}
            onSelectTruck={handleSelectTruck}
            replayTimeMs={replayTimeMs}
            selectedJobId={selectedJobId}
            sites={sites}
            trucks={trucks}
          />
        </Box>
      )}
    </Box>
  );
});

const Centered = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      minHeight: 240,
      p: 2,
    }}
  >
    {children}
  </Box>
);

export default TruckReplayTab;
